/**
 * A fila de pedidos.
 *
 * É o que faz o barbeiro abrir o app: alguém pediu horário e está esperando
 * resposta. Cada pedido resolve em um toque, sem abrir nada.
 */

import type { AppointmentView } from '@barbearia/shared';
import { describeServices } from '@barbearia/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ScreenHeader } from '../../App.tsx';
import { Badge, Button, EmptyState, Spinner } from '../../components/ui.tsx';
import { api, subscribeToAgenda } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { duration, longDateOf, timeOf } from '../../lib/format.ts';
import { RequestReviewSheet } from './RequestReviewSheet.tsx';
import { SeriesSkipsSection } from './Series.tsx';
import { WaitlistSection } from './Waitlist.tsx';

export function Pending() {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['pending'],
    queryFn: () => api.get<{ appointments: AppointmentView[] }>('/appointments/pending'),
  });

  useEffect(() => {
    return subscribeToAgenda(() => {
      queryClient.invalidateQueries({ queryKey: ['pending'] });
      queryClient.invalidateQueries({ queryKey: ['pending-count'] });
    });
  }, [queryClient]);

  const [reviewing, setReviewing] = useState<AppointmentView | null>(null);

  // A API já devolve em ordem cronológica: o pedido mais próximo primeiro, que
  // é o que precisa de resposta antes.
  const appointments = data?.appointments ?? [];

  return (
    <div className="flex min-h-full flex-col pb-6">
      <ScreenHeader
        title="Pedidos"
        subtitle={
          appointments.length > 0
            ? `${appointments.length} aguardando você`
            : 'Nada aguardando resposta'
        }
      />

      {isPending && <Spinner />}

      {!isPending && appointments.length === 0 && (
        <EmptyState
          title="Tudo respondido"
          description="Quando um cliente pedir horário pelo app, ele aparece aqui — e o aviso acende sozinho, sem precisar atualizar."
        />
      )}

      <ul className="flex flex-col gap-3 px-4 pt-4">
        {appointments.map((appointment) => (
          <li
            key={appointment.id}
            className="overflow-hidden rounded-2xl border border-warn/40 bg-warn/5"
          >
            <div className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[16px] font-semibold text-ink">
                    {appointment.client.name}
                  </p>
                  <p className="text-[14px] text-ink-soft">
                    {describeServices(appointment.services.map((service) => service.name))} ·{' '}
                    {duration(appointment.durationMinutes)}
                  </p>
                </div>
                {appointment.client.totalNoShows > 0 && (
                  <Badge tone="danger">{appointment.client.totalNoShows} faltas</Badge>
                )}
              </div>

              <p className="pt-2 text-[15px] font-medium text-ink">
                {longDateOf(appointment.startsAt.slice(0, 10), tenant.timezone)} às{' '}
                <span className="tabular-nums">
                  {timeOf(appointment.startsAt, tenant.timezone)}
                </span>
              </p>

              {appointment.notes && (
                <p className="pt-1 text-[14px] text-ink-soft">“{appointment.notes}”</p>
              )}
            </div>

            <div className="border-t border-warn/30 p-3">
              {/* Responder abre a revisão: confirmar sem ver o que há em volta
                  é como marcar de olhos fechados. */}
              <Button full onClick={() => setReviewing(appointment)}>
                Responder
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <SeriesSkipsSection />

      <WaitlistSection />

      {reviewing && <RequestReviewSheet request={reviewing} onClose={() => setReviewing(null)} />}
    </div>
  );
}
